import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, InputAdornment, MenuItem, Select, TextField, Tooltip, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { ROLE_LABELS, ROLE_SELECT_MENU_PROPS, getAssignableRoles } from "../utils/roleOptions";
import { getApiErrorMessage } from "../utils/apiError";

const EMPTY_FORM = {
  email: "",
  password: "",
  roles: ["brd"],
  is_approved: true,
  max_sessions: 1,
  input_token_limit: "4.0",
  output_token_limit: "2.0",
  send_welcome_email: true,
};

const PW_LOWER = "abcdefghijkmnopqrstuvwxyz";
const PW_UPPER = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const PW_DIGITS = "23456789";
const PW_SYMBOLS = "!@#$%&*?";

// Unbiased random index using the browser's cryptographic generator.
function secureRandomIndex(max) {
  const limit = Math.floor(0x100000000 / max) * max;
  const buf = new Uint32Array(1);
  do {
    window.crypto.getRandomValues(buf);
  } while (buf[0] >= limit);
  return buf[0] % max;
}

// 12-char temporary password with at least one lower, upper, digit and symbol
// (ambiguous characters such as 0/O and 1/l/I are left out so it is easy to read out loud).
function generateTempPassword(length = 12) {
  const pools = [PW_LOWER, PW_UPPER, PW_DIGITS, PW_SYMBOLS];
  const all = pools.join("");
  const chars = pools.map((pool) => pool[secureRandomIndex(pool.length)]);
  while (chars.length < length) chars.push(all[secureRandomIndex(all.length)]);
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = secureRandomIndex(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}

// Token limits are typed in millions (e.g. "4.0") and sent as raw token counts.
function normalizeTokenLimit(value) {
  const parsed = parseFloat(String(value ?? "").replace(/,/g, ""));
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.floor(parsed * 1_000_000);
}

/**
 * Create a user directly (no sign-up). The backend records the creator, so a User Admin
 * can afterwards manage the accounts they create here.
 */
export default function AddUserDialog({ open, onClose, onCreated, isDark, isSuperAdmin, isUserAdmin }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Set after a successful create when a welcome email was requested: { email, sent, error }
  const [result, setResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setError("");
      setResult(null);
      setShowPassword(false);
      setCopied(false);
    }
  }, [open]);

  // Only a super admin can hand out the admin / user_admin roles; a User Admin gets the signup roles only.
  const assignableRoles = useMemo(() => getAssignableRoles({ isSuperAdmin, isUserAdmin }), [isSuperAdmin, isUserAdmin]);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleGeneratePassword = () => {
    update("password", generateTempPassword());
    setShowPassword(true);
    setCopied(false);
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(form.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setError("Could not copy the password. Select it and copy it manually.");
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!form.email.trim() || !form.password.trim()) {
      setError("Email and Password are required.");
      return;
    }
    if (form.roles.length === 0) {
      setError("Select at least one role.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("edge_token");
      const inputLimit = normalizeTokenLimit(form.input_token_limit);
      const outputLimit = normalizeTokenLimit(form.output_token_limit);
      const res = await axios.post(
        "/api/admin/users",
        {
          email: form.email.trim(),
          password: form.password,
          roles: form.roles,
          is_approved: form.is_approved,
          max_sessions: Number(form.max_sessions || 1),
          input_token_limit: inputLimit,
          output_token_limit: outputLimit,
          total_token_limit: inputLimit + outputLimit,
          send_welcome_email: form.send_welcome_email,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (onCreated) onCreated(res.data);
      if (form.send_welcome_email) {
        // Keep the dialog open so the creator can see whether the email went out.
        setResult({
          email: res.data.email,
          sent: Boolean(res.data.welcome_email_sent),
          error: res.data.welcome_email_error || "",
        });
      } else {
        onClose();
      }
    } catch (err) {
      console.error(err);
      setError(getApiErrorMessage(err, "Failed to create user."));
    } finally {
      setLoading(false);
    }
  };

  const border = isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(15, 23, 42, 0.08)";

  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          bgcolor: isDark ? "#0f172a" : "#ffffff",
          backgroundImage: "none",
          border: `1px solid ${border}`,
          boxShadow: isDark ? "0 24px 60px rgba(0,0,0,0.6)" : "0 20px 50px rgba(15,23,42,0.15)",
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary" }}>
            Create New User Account
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {isSuperAdmin
              ? "Create and assign initial permissions to a new user account."
              : "This user will be associated with your User Admin account."}
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={loading} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: "var(--ae-border)" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>
            {error}
          </Alert>
        )}
        {result ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, py: 1 }}>
            <Alert severity="success" sx={{ borderRadius: "10px" }}>
              Account created for <b>{result.email}</b>.
            </Alert>
            {result.sent ? (
              <Alert severity="success" sx={{ borderRadius: "10px" }}>
                Login details were emailed to {result.email}.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ borderRadius: "10px" }}>
                The account was created, but the login email was not sent
                {result.error ? `: ${result.error}` : "."} Please share the login details with the user yourself.
              </Alert>
            )}
          </Box>
        ) : (
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            label="Email Address" required fullWidth size="small" type="email" placeholder="user@example.com"
            value={form.email} onChange={(e) => update("email", e.target.value)}
          />
          <TextField
            label="Password" required fullWidth size="small" type={showPassword ? "text" : "password"} placeholder="••••••••"
            value={form.password} onChange={(e) => update("password", e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={showPassword ? "Hide password" : "Show password"}>
                    <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end">
                      {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={copied ? "Copied!" : "Copy password"}>
                    <span>
                      <IconButton size="small" onClick={handleCopyPassword} disabled={!form.password}>
                        <ContentCopyIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Generate a random temporary password">
                    <IconButton size="small" onClick={handleGeneratePassword} sx={{ color: "#F26522" }}>
                      <AutorenewRoundedIcon sx={{ fontSize: 19 }} />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: "text.secondary", fontSize: 13 }}>
              Assign Roles / Permissions
            </Typography>
            <Select
              multiple fullWidth size="small" value={form.roles}
              onChange={(e) => update("roles", typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value)}
              renderValue={(selected) => selected.map((role) => ROLE_LABELS[role] || role).join(", ")}
              MenuProps={ROLE_SELECT_MENU_PROPS}
            >
              {assignableRoles.map((role) => (
                <MenuItem key={role.value} value={role.value}>{role.label}</MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Max Active Sessions" size="small" type="number" inputProps={{ min: 1, max: 5 }}
              value={form.max_sessions} onChange={(e) => update("max_sessions", parseInt(e.target.value, 10) || 1)}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Input Limit (Millions)" size="small" type="number" inputProps={{ step: "0.5", min: "0" }}
              value={form.input_token_limit} onChange={(e) => update("input_token_limit", e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Output Limit (Millions)" size="small" type="number" inputProps={{ step: "0.5", min: "0" }}
              value={form.output_token_limit} onChange={(e) => update("output_token_limit", e.target.value)}
              sx={{ flex: 1 }}
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.is_approved}
                onChange={(e) => update("is_approved", e.target.checked)}
                sx={{ color: "#F26522", "&.Mui-checked": { color: "#F26522" } }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: 13, color: "text.primary" }}>
                Approve account immediately (User can log in right away)
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.send_welcome_email}
                onChange={(e) => update("send_welcome_email", e.target.checked)}
                sx={{ color: "#F26522", "&.Mui-checked": { color: "#F26522" } }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: 13, color: "text.primary" }}>
                Email the login details (username, password and portal link) to the user
              </Typography>
            }
            sx={{ mt: -1 }}
          />
        </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {result ? (
          <Button
            onClick={onClose} variant="contained"
            sx={{ background: "#F26522", "&:hover": { background: "#d85417" }, fontWeight: 700, px: 3 }}
          >
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={loading} sx={{ color: "text.secondary" }}>Cancel</Button>
            <Button
              onClick={handleSubmit} variant="contained" disabled={loading}
              sx={{ background: "#F26522", "&:hover": { background: "#d85417" }, fontWeight: 700, px: 3 }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Create Account"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
